/**
 * Stack and region thumbnail composition utility.
 *
 * Provides two pure functions for rendering small thumbnails from the
 * label-plane binary mask:
 *
 *   composeRegionThumbnail - single region, uses one member's label and RGB.
 *   composeStackThumbnail  - union of all member labels; uses first member's RGB.
 *
 * Both functions are pure: no DOM, no globals, no side effects.
 * Uses `new ImageData(width, height)` which is available in the main thread,
 * Web Workers, Tauri webview, and happy-dom test environments.
 */

import type { RegionEntry } from '../state';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface RegionThumbnailInput {
  entry: RegionEntry;
  regionLabelPlane: Uint8Array;
  width: number;
  height: number;
  outputSize: number;
}

export interface StackThumbnailInput {
  /** All RegionEntry objects belonging to the stack. */
  members: RegionEntry[];
  regionLabelPlane: Uint8Array;
  width: number;
  height: number;
  outputSize: number;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compose a thumbnail for a single region.
 *
 * Returns an ImageData of size `outputSize × outputSize`. In-mask pixels
 * (where `regionLabelPlane[i] === entry.label`) are rendered with the entry's
 * RGB at full opacity; all other pixels are transparent.
 *
 * Uses nearest-neighbour downsampling for speed and sharp visual clarity.
 */
export function composeRegionThumbnail(input: RegionThumbnailInput): ImageData {
  const { entry, regionLabelPlane, width, height, outputSize } = input;
  const labelSet = new Set([entry.label]);
  return composeMaskThumbnail(entry.rgb, labelSet, regionLabelPlane, width, height, outputSize);
}

/**
 * Compose a combined-mask thumbnail for a stack.
 *
 * A pixel is "in stack" if `regionLabelPlane[i]` matches any member's label.
 * The representative colour is the first member's RGB (or neutral grey when
 * the members array is empty).
 *
 * Returns an ImageData of size `outputSize × outputSize`.
 */
export function composeStackThumbnail(input: StackThumbnailInput): ImageData {
  const { members, regionLabelPlane, width, height, outputSize } = input;
  const labelSet = new Set(members.map((m) => m.label));
  const rgb: [number, number, number] = members[0]?.rgb ?? [128, 128, 128];
  return composeMaskThumbnail(rgb, labelSet, regionLabelPlane, width, height, outputSize);
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Core thumbnail composer: build a binary mask from `labelSet`, downsample
 * it to `outputSize × outputSize` using nearest-neighbour sampling, and
 * tint in-mask pixels with `rgb`.
 *
 * The full plane is scanned to find the bounding box of the mask, then the
 * bbox is mapped into the output square while preserving aspect ratio.
 */
function composeMaskThumbnail(
  rgb: [number, number, number],
  labelSet: Set<number>,
  labelPlane: Uint8Array,
  width: number,
  height: number,
  outputSize: number,
): ImageData {
  if (labelPlane.length === 0 || width === 0 || height === 0 || outputSize === 0) {
    return new ImageData(outputSize || 1, outputSize || 1);
  }

  // --- Phase 1: find bounding box ---
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    const rowBase = y * width;
    for (let x = 0; x < width; x++) {
      if (labelSet.has(labelPlane[rowBase + x]!)) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  const data = new Uint8ClampedArray(outputSize * outputSize * 4);

  // No pixels found - return all-transparent thumbnail.
  if (maxX < 0) {
    return new ImageData(data, outputSize, outputSize);
  }

  // --- Phase 2: nearest-neighbour downscale into bbox ---
  const bw = maxX - minX + 1;
  const bh = maxY - minY + 1;

  const scale = outputSize / Math.max(bw, bh);
  const scaledW = bw * scale;
  const scaledH = bh * scale;
  const offsetX = (outputSize - scaledW) / 2;
  const offsetY = (outputSize - scaledH) / 2;

  const [r, g, b] = rgb;

  for (let ty = 0; ty < outputSize; ty++) {
    for (let tx = 0; tx < outputSize; tx++) {
      const bx = tx - offsetX;
      const by = ty - offsetY;

      if (bx < 0 || by < 0 || bx >= scaledW || by >= scaledH) continue;

      // Nearest-neighbour: sample from centre of each destination pixel.
      const srcX = minX + Math.floor((bx + 0.5) / scale);
      const srcY = minY + Math.floor((by + 0.5) / scale);

      const clampedX = Math.min(srcX, maxX);
      const clampedY = Math.min(srcY, maxY);

      if (labelSet.has(labelPlane[clampedY * width + clampedX]!)) {
        const i = (ty * outputSize + tx) * 4;
        data[i]     = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = 255;
      }
    }
  }

  return new ImageData(data, outputSize, outputSize);
}
